import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Spinner, Table } from "react-bootstrap";
import Dropzone from "react-dropzone";
import { BsFillFileEarmarkArrowDownFill, BsFillTrashFill, BsPencilFill, BsCheckLg, BsXLg } from "react-icons/bs";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import useConfirm from "../../organisms/ConfirmDialog/use-confirm";
import { selectIsAdmin, selectCurentUserRoles } from "../../../../store/slices/auth-slice";

const baseStyle: any = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

/**
 * Reusable file-attachment panel for legacy dbo.GlobalAttachments-backed modules
 * (e.g. Orders). Looks like the He3 EntityAttachments card (dropzone + table),
 * but the storage is the legacy GlobalAttachments store — supplied entirely via
 * `field` config so it can be reused for any legacy attachment module.
 *
 * Config (on `field`):
 *   title            - card header text (default "Attachments")
 *   moduleIdField    - the form field holding the owning entity id (default "id")
 *   permissions      - { write:number[] } role gate for upload/delete
 *   requests: {
 *     list(moduleId)                 => [{ id, fileName, moduleKey }]
 *     upload(moduleId, description, file)
 *     download(id, fileName)
 *     remove(id)
 *     updateDescription(id, description)   (optional - enables inline edit)
 *   }
 */
export const LegacyAttachments = (props: any) => {
  const { field, watch, confirm: confirmProp } = props;
  const { confirm: confirmHook } = useConfirm();
  const confirm = confirmProp ?? confirmHook;

  const title = field?.title ?? "Attachments";
  const moduleIdField = field?.moduleIdField ?? "id";
  const permissions = field?.permissions;
  const requests = field?.requests ?? {};

  const moduleId = watch?.(moduleIdField);

  const isAdmin = useSelector(selectIsAdmin);
  const currRoles = useSelector(selectCurentUserRoles);
  const writeAccess = useMemo(() => {
    if (!permissions || !permissions.write || !currRoles) return true;
    return !!currRoles.find((r: any) => permissions.write.includes(r.roleId));
  }, [currRoles, permissions]);
  const restricted = !writeAccess && !isAdmin;

  const [items, setItems] = useState<any[]>([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState("");

  const load = useCallback(async () => {
    if (!moduleId || !requests.list) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await requests.list(moduleId);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    load();
  }, [load]);

  const onDrop = async (files: any[]) => {
    const accepted = (files ?? []).filter(Boolean);
    if (!accepted.length || !moduleId || !requests.upload) return;
    setLoading(true);
    let uploaded = 0;
    const failed: string[] = [];
    // Upload sequentially so each file is a distinct GlobalAttachments row and a
    // single failure doesn't abort the rest of the batch.
    for (const file of accepted) {
      try {
        await requests.upload(moduleId, description, file);
        uploaded += 1;
      } catch (err: any) {
        const message = err?.response?.data ?? err?.message;
        failed.push(`${file?.name ?? "file"}: ${typeof message === "string" ? message : "upload failed"}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`Successfully uploaded ${uploaded} attachment${uploaded === 1 ? "" : "s"}`);
      setDescription("");
    }
    if (failed.length) {
      toast.error(`Failed to upload ${failed.length} file${failed.length === 1 ? "" : "s"}`);
    }
    await load();
    setLoading(false);
  };

  const onDownload = async (row: any) => {
    if (!requests.download) return;
    await requests.download(row.id, row.fileName);
  };

  const onDelete = async (row: any) => {
    if (!requests.remove) return;
    const ok = await confirm("Are you sure you want to permanently delete this file?");
    if (!ok) return;
    setLoading(true);
    try {
      await requests.remove(row.id);
      toast.success("Successfully deleted attachment");
      await load();
    } catch {
      toast.error("Failed to delete attachment");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (row: any) => {
    setEditingId(row.id);
    setEditingDescription(row.moduleKey ?? "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingDescription("");
  };

  const saveEdit = async (row: any) => {
    if (!requests.updateDescription) return;
    setLoading(true);
    try {
      await requests.updateDescription(row.id, editingDescription);
      toast.success("Successfully updated description");
      cancelEdit();
      await load();
    } catch {
      toast.error("Failed to update description");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mt-2 card-primary card-outline flex-grow-1 d-flex flex-column overflow-auto">
      <Spinner
        animation="border"
        variant="primary"
        className={`create-spinner${loading ? " visible" : " invisible"}`}
      />
      <Card.Header>
        <h3 className="card-title">{title}</h3>
      </Card.Header>
      <Card.Body className="d-flex flex-column overflow-auto">
        {!restricted && (
          <>
            <Dropzone onDrop={(accepted: any) => onDrop(accepted)}>
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps({ style: baseStyle })}>
                    <input {...getInputProps()} />
                    <p>Click or Drag &amp; Drop files here...</p>
                  </div>
                </section>
              )}
            </Dropzone>
            <Form.Group className="mt-2 mb-2">
              <Form.Label className="mb-1">Description</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                maxLength={200}
                value={description}
                placeholder="Optional file description"
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>
          </>
        )}
        <div className="d-flex flex-column flex-grow-1 overflow-auto">
          <Table className="grid" size="sm" hover>
            <thead>
              <tr>
                <th style={{ width: "120px" }} />
                <th>File Name</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-muted">
                    No attachments.
                  </td>
                </tr>
              )}
              {items.map((row) => {
                const isEditing = editingId === row.id;
                return (
                  <tr key={row.id}>
                    <td>
                      <div className="d-flex">
                        {!restricted && requests.updateDescription && (
                          <>
                            {isEditing ? (
                              <>
                                <Button
                                  // @ts-ignore
                                  size="xsm"
                                  variant="success"
                                  title="Save description"
                                  onClick={() => saveEdit(row)}
                                >
                                  <BsCheckLg />
                                </Button>
                                &nbsp;
                                <Button
                                  // @ts-ignore
                                  size="xsm"
                                  variant="secondary"
                                  title="Cancel"
                                  onClick={cancelEdit}
                                >
                                  <BsXLg />
                                </Button>
                                &nbsp;
                              </>
                            ) : (
                              <>
                                <Button
                                  // @ts-ignore
                                  size="xsm"
                                  variant="secondary"
                                  title="Edit description"
                                  onClick={() => startEdit(row)}
                                >
                                  <BsPencilFill />
                                </Button>
                                &nbsp;
                              </>
                            )}
                          </>
                        )}
                        {!restricted && !isEditing && (
                          <>
                            <Button
                              // @ts-ignore
                              size="xsm"
                              variant="secondary"
                              title="Delete file"
                              onClick={() => onDelete(row)}
                            >
                              <BsFillTrashFill />
                            </Button>
                            &nbsp;
                          </>
                        )}
                        {!isEditing && (
                          <Button
                            // @ts-ignore
                            size="xsm"
                            variant="secondary"
                            title="Download file"
                            onClick={() => onDownload(row)}
                          >
                            <BsFillFileEarmarkArrowDownFill />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td>{row.fileName}</td>
                    <td>
                      {isEditing ? (
                        <Form.Control
                          size="sm"
                          type="text"
                          maxLength={200}
                          autoFocus
                          value={editingDescription}
                          placeholder="File description"
                          onChange={(e) => setEditingDescription(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit(row);
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                      ) : (
                        row.moduleKey
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};
